// navVisibility.ts
// Shared utility to determine which navbar items are visible to a user
// This ensures Home page only shows tabs that are visible in the navbar

import { useClient } from '../contexts/ClientContext';
import { useCompanyFeatures } from '../contexts/CompanyFeaturesContext';
import { isAdminRole, isMainAccountLevel, canSeeAdminContent, hasFullPermissions } from '../config/permissionsConfig';

export type NavItem = {
  to: string;
  text: string;
  roles?: string[];
  permAll?: string[];
  permAny?: string[];
  feature?: string;
};

/**
 * Checks if a navbar item should be visible based on user role, permissions, and features
 * Now uses hasFeature which combines plan features with enabled features
 * 
 * IMPORTANT: Managers with Full Access (גישה מלאה) can see everything like admin.
 */
export function canSeeNavItem(
  item: NavItem,
  role: string | undefined,
  permissions: string[],
  hasFeature: (feature: string) => boolean,
  hiddenFromSidebar: string[] = [],
  user?: { role?: string; email?: string; permissions?: string[] } | null
): boolean {
  // Superadmin items are ONLY for superadmin - never show to regular admins or managers
  if (item.roles && item.roles.includes('superadmin') && role !== 'superadmin') {
    return false;
  }
  
  // Check if manager has full access (all permissions) - they should see everything like admin
  const isFullAccessManager = role === 'manager' && hasFullPermissions(user?.permissions || permissions || []);
  
  // Admin-only items: admins, superadmins, main account, OR managers with full access can see them
  if (item.roles && item.roles.includes('admin') && !item.roles.includes('manager')) {
    // This is an admin-only item (not shared with managers)
    // But managers with full access can see it
    if (isFullAccessManager) {
      // Manager with full access can see admin items
    } else if (!canSeeAdminContent(user || { role, email: undefined })) {
      return false;
    }
  }
  
  // Managers with full access: see everything like admin (admin items + manager items)
  if (isFullAccessManager) {
    // Show admin items and manager items
    if (item.roles && item.roles.length > 0) {
      return item.roles.includes('admin') || item.roles.includes('manager');
    }
    return true;
  }
  
  // Check role - regular managers can only access their own items
  if (item.roles && item.roles.length > 0 && !item.roles.includes(role || '')) {
    return false;
  }

  // Skip permission checks for admins and superadmins only
  if (role !== 'admin' && role !== 'superadmin') {
    // Check permissions (all required)
    if (item.permAll && !item.permAll.every(p => permissions.includes(p))) {
      return false;
    }

    // Check permissions (any required)
    if (item.permAny && !item.permAny.some(p => permissions.includes(p))) {
      return false;
    }
  }

  // Check feature access using plan features + enabled features
  if (item.feature) {
    // Respect explicit hide-from-sidebar rule first
    if (hiddenFromSidebar.includes(item.feature)) {
      return false;
    }

    // Use hasFeature which combines plan features with enabled features
    return hasFeature(item.feature);
  }

  return true;
}

/**
 * Returns all visible navbar items for the current user
 * This matches the logic in Sidebar.tsx
 */
export function getVisibleNavItems(
  allNavItems: NavItem[],
  role: string | undefined,
  permissions: string[],
  hasFeature: (feature: string) => boolean,
  hiddenFromSidebar: string[] = [],
  user?: { role?: string; email?: string } | null
): NavItem[] {
  const visible: NavItem[] = [];
  for (const item of allNavItems) {
    if (!canSeeNavItem(item, role, permissions, hasFeature, hiddenFromSidebar, user)) {
      continue;
    }
    visible.push(item);
  }
  return visible;
}

/**
 * Hook to get visible navbar items
 */
export function useVisibleNavItems(allNavItems: NavItem[]): NavItem[] {
  const { client } = useClient();
  const { hasFeature, hiddenFromSidebar } = useCompanyFeatures();
  const role = client?.role;
  const permissions = client?.permissions || [];

  return getVisibleNavItems(allNavItems, role, permissions, hasFeature, hiddenFromSidebar, client);
}

